import { CloseOutlined, GlobalOutlined, MessageOutlined } from '@ant-design/icons';
import { Form } from 'antd';
import * as React from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getActiveUser } from '../../../../react4xp/parts/admin-header/actions';
import { IUser } from '../../../../react4xp/parts/admin-users/admin-users.type';
import { getSectionsApi } from '../../knowledge-base-common/actions';
import { sendEmail } from '../../main/actions';
import { EmailSource } from '../../main/actions.types';
import { ASSISTANT_REPLY_DELAY } from '../virtual-assistant.constants';
import { delay, fromAssistant } from '../virtual-assistant.utils';
import { ActionTypes, EmailFormData, From, Message, Tabs } from '../vitrual-assistant.type';
import Chat from './chat';
import LanguageSelector from './language-selector';
import OperatorRequestForm from './operator-request-form';
import { localize } from '/react4xp/mixins/helpers';
import Button from '/react4xp/mixins/ui/button/button';
import { PartContext } from '/react4xp/parts/virtual-assistant/virtual-assistant';

interface VirtualAssistantOpenProps {
    close: () => void;
    translations: any;
    language: string;
    token: string;
}

export default function VirtualAssistantOpen({
    close,
    translations,
    language,
    token,
}: Readonly<VirtualAssistantOpenProps>) {
    const [activeTab, setActiveTab] = useState<Tabs>(Tabs.CHAT);
    const assistantRef = useRef<HTMLDivElement>(null);

    const handleResizeStart = (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0 || !assistantRef.current) return;

        if (window.matchMedia('(max-width: 480px)').matches) return;

        event.preventDefault();

        const element = assistantRef.current;
        const rect = element.getBoundingClientRect();

        const startX = event.clientX;
        const startY = event.clientY;

        const startWidth = rect.width;
        const startHeight = rect.height;

        const computed = window.getComputedStyle(element);

        const rightOffset = Math.max(0, parseFloat(computed.right) || 0);
        const bottomOffset = Math.max(0, parseFloat(computed.bottom) || 0);

        const parent = element.parentElement;
        const parentRect = parent?.getBoundingClientRect();

        const extraWidth = parentRect
            ? Math.max(0, parentRect.width - rect.width)
            : 0;

        const parentStyle = parent
            ? window.getComputedStyle(parent)
            : null;

        const parentRight = parseFloat(parentStyle?.right || '') || 24;
        const parentBottom = parseFloat(parentStyle?.bottom || '') || 24;

        const maxWidth = Math.max(
            200,
            window.innerWidth - parentRight - extraWidth - 16
        );

        const maxHeight = Math.max(
            200,
            window.innerHeight - parentBottom - 16
        );

        const handlePointerMove = (moveEvent: PointerEvent) => {
            const deltaX = startX - moveEvent.clientX;
            const deltaY = startY - moveEvent.clientY;

            const minWidth = Math.min(320, maxWidth);
            const minHeight = Math.min(350, maxHeight);

            const width = Math.max(
                minWidth,
                Math.min(maxWidth, startWidth + deltaX)
            );

            const height = Math.max(
                minHeight,
                Math.min(maxHeight, startHeight + deltaY)
            );

            element.style.width = `${width}px`;
            element.style.height = `${height}px`;
        };

        const handlePointerUp = () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            window.removeEventListener('pointercancel', handlePointerUp);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        window.addEventListener('pointercancel', handlePointerUp);
    };
    const [emailForm] = Form.useForm<EmailFormData>();
    const [isValid, setIsValid] = useState<boolean>(false);
    const [messages, setMessages] = useState<Message[]>(() => {
        const messages = [
            fromAssistant(localize('messages.greetingAI', translations)),
        ];

        return messages.reverse().map((message, index) => ({
            id: +1 + index,
            ...message,
        }));
    });
    const [isTyping, setIsTyping] = useState<boolean>();
    const latestMessages = useRef<Message[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [activeUser, setActiveUser] = useState<IUser>(null);

    useEffect(() => {
        if (token) {
            getActiveUser()
                .then((res) => res.data)
                .then((res) => {
                    emailForm.setFieldsValue({
                        email: res.email,
                        name: res.fullname,
                    });

                    setActiveUser(res);
                });
        }
    }, []);

    const mainActions = [
        <Button
            icon={<MessageOutlined />}
            onClick={() => setActiveTab(Tabs.CHAT)}
            type="text"
            uppercase={false}
            style={{
                color: '#3457B1',
            }}
        >
            {localize('chat', translations)}
        </Button>,
        <Button
            icon={<GlobalOutlined size={20} />}
            onClick={() => setActiveTab(Tabs.LANGUAGE)}
            type="text"
            uppercase={false}
        >
            {localize('language-selector', translations)}
        </Button>,
    ];

    const emailOperator = useCallback(() => {
        setActiveTab(Tabs.OPERATOR);
    }, [translations, language]);

    const handleSendEmail = useCallback(() => {
        setIsLoading(true);

        const values = emailForm.getFieldsValue();
        const formData = new FormData();

        formData.append('identifier', activeUser && activeUser.login);
        formData.append('content', values.content);
        formData.append('phoneNumber', values.phoneNumber);
        formData.append('email', values.email);
        formData.append('source', EmailSource.VirtualAssistant);

        sendEmail(formData)
            .then(() => {
                addMessage(fromAssistant(localize('messages.message-sent', translations)));
                emailForm.resetFields();
                setActiveTab(Tabs.CHAT);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [emailForm]);

    const handleCancel = useCallback(() => {
        emailForm.resetFields();
        setActiveTab(Tabs.CHAT);
    }, [emailForm]);

    const operatorFormActions = [
        <Button
            onClick={handleSendEmail}
            type="primary"
            disabled={!isValid}
            loading={isLoading}
        >
            {localize('button.send', translations)}
        </Button>,
        <Button
            onClick={handleCancel}
            loading={isLoading}
        >
            {localize('cancel', translations)}
        </Button>,
    ];

    const addMessage = useCallback(
        async (...messages: Array<Omit<Message, 'id'> & { id?: number }>) => {
            setMessages(prev => [
                ...messages.map(msg => ({
                    id: Date.now() + Math.random(),
                    ...msg,
                })),
                ...prev,
            ]);

            if (
                messages[0]?.from === From.ASSISTANT &&
                messages[0]?.status !== 'streaming'
            ) {
                setIsTyping(true);
                await delay(ASSISTANT_REPLY_DELAY);
                setIsTyping(false);
            }
        },
        []
    );

    const updateMessage = useCallback(
        (id: number, patch: Partial<Message>) => {
            setMessages(prev =>
                prev.map(msg =>
                    msg.id === id ? { ...msg, ...patch } : msg
                )
            );
        },
        []
    );

    useEffect(() => {
        latestMessages.current = messages;
    }, [messages]);

    const saveMessageHistory = useCallback(() => {
        if (!latestMessages?.current.length) return;
        sessionStorage.setItem('virtualAssistantMessages', JSON.stringify(latestMessages.current));
    }, []);

    useEffect(() => {
        // addMessage(
        //     fromAssistant(localize('messages.greeting', translations)),
        //     fromAssistant(localize('messages.kb-link', translations), {
        //         type: ActionTypes.KNOWLEDGE_BASE,
        //     }),
        //     fromAssistant(localize('messages.consultations', translations), {
        //         type: ActionTypes.CONSULTATIONS,
        //     })
        // );

        window.addEventListener('beforeunload', saveMessageHistory);

        return () => {
            window.removeEventListener('beforeunload', saveMessageHistory);
            saveMessageHistory();
        };
    }, [saveMessageHistory]);

    return (
        <div className="assistant-ai" ref={assistantRef}>
            <div
                className="assistant-ai_resize_handle"
                onPointerDown={handleResizeStart}
                title="Потяните, чтобы изменить размер окна"
                aria-label="Изменить размер окна"
            />

            <div className="virtual-assistant_header">
                <span style={{ textTransform: 'uppercase' }}>
                    {localize('ii', translations)}
                </span>

                <CloseOutlined
                    onClick={close}
                    className="virtual-assistant__close_button"
                />
            </div>

            <div
                style={{
                    display: 'flex',
                    flexGrow: 1,
                    flexDirection: 'column',
                    minHeight: 0,
                    minWidth: 0,
                }}
            >
                <Chat
                    messages={messages}
                    updateMessage={updateMessage}
                    addMessage={addMessage}
                    emailOperator={emailOperator}
                    isTyping={isTyping}
                    translations={translations}
                    isAuthenticated={Boolean(token)}
                />
            </div>
        </div>
    );
}
